import { supabase } from "@/integrations/supabase/client";
import * as Sentry from '@sentry/react';

type Listener = {
  onConversationChange?: (payload: any) => void;
  onMessageChange?: (payload: any) => void;
  onLeadChange?: (payload: any) => void;
  onTagChange?: (payload: any) => void;
  onTagRelationChange?: (payload: any) => void;
  onNoteChange?: (payload: any) => void;
};

type OrgEntry = {
  channel: ReturnType<typeof supabase.channel> | null;
  listeners: Set<Listener>;
  refCount: number;
  paused: boolean;
};

class ChatRealtimeManager {
  private registry = new Map<string, OrgEntry>();

  register(orgId: string, listener: Listener) {
    if (!orgId) return () => {};

    let entry = this.registry.get(orgId);
    if (!entry) {
      entry = {
        channel: null,
        listeners: new Set<Listener>(),
        refCount: 0,
        paused: false,
      };
      this.registry.set(orgId, entry);
    }

    entry.listeners.add(listener);
    entry.refCount += 1;

    // Connect channel if not paused and not already connected
    if (!entry.paused && !entry.channel) {
      this.createChannel(orgId, entry);
    }

    return () => {
      const current = this.registry.get(orgId);
      if (!current) return;
      current.listeners.delete(listener);
      current.refCount -= 1;
      if (current.refCount <= 0) {
        this.teardown(current);
        this.registry.delete(orgId);
      }
    };
  }

  /** Pause: disconnect the real Supabase channel to free resources */
  pause() {
    this.registry.forEach((entry) => {
      if (!entry.paused) {
        entry.paused = true;
        this.teardown(entry);
      }
    });
    if (import.meta.env.DEV) console.info("[ChatRealtimeManager] paused — channels disconnected");
  }

  /** Resume: reconnect channels for all registered orgs */
  resume() {
    this.registry.forEach((entry, orgId) => {
      if (entry.paused) {
        entry.paused = false;
        if (!entry.channel && entry.refCount > 0) {
          this.createChannel(orgId, entry);
        }
      }
    });
    if (import.meta.env.DEV) console.info("[ChatRealtimeManager] resumed — channels reconnected");
  }

  /** Returns true if all registered entries are paused */
  isPaused(): boolean {
    if (this.registry.size === 0) return false;
    let allPaused = true;
    this.registry.forEach((entry) => {
      if (!entry.paused) allPaused = false;
    });
    return allPaused;
  }

  /** Debug helper: returns channel status per orgId */
  getStatus(): Record<string, { refCount: number; listeners: number; paused: boolean; connected: boolean }> {
    const out: Record<string, { refCount: number; listeners: number; paused: boolean; connected: boolean }> = {};
    this.registry.forEach((entry, orgId) => {
      out[orgId] = {
        refCount: entry.refCount,
        listeners: entry.listeners.size,
        paused: entry.paused,
        connected: entry.channel !== null,
      };
    });
    return out;
  }

  private createChannel(orgId: string, entry: OrgEntry) {
    const notify = (fn: keyof Listener, payload: any) => {
      if (entry.paused) return;
      entry.listeners.forEach((l) => l[fn]?.(payload));
    };

    // Single channel with multiple .on() listeners — 5 tables, 1 Supabase channel
    entry.channel = supabase
      .channel(`chat-org-${orgId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "chat_conversations", filter: `org_id=eq.${orgId}` },
        (payload) => notify("onConversationChange", payload)
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "chat_messages", filter: `org_id=eq.${orgId}` },
        (payload) => notify("onMessageChange", payload)
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "leads", filter: `org_id=eq.${orgId}` },
        (payload) => notify("onLeadChange", payload)
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "chat_conversation_tags", filter: `org_id=eq.${orgId}` },
        (payload) => notify("onTagChange", payload)
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "chat_conversation_tag_relations", filter: `org_id=eq.${orgId}` },
        (payload) => notify("onTagRelationChange", payload)
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "chat_conversation_notes", filter: `org_id=eq.${orgId}` },
        (payload) => notify("onNoteChange", payload)
      )
      .subscribe((status) => {
        if (import.meta.env.DEV) console.info(`[ChatRealtimeManager] chat-org-${orgId}:`, status);
        if (status === 'CHANNEL_ERROR') {
          Sentry.captureMessage('Realtime channel error', {
            level: 'warning',
            tags: { module: 'realtime', channel: `chat-org-${orgId}` },
          });
        }
        if (status === 'TIMED_OUT') {
          Sentry.captureMessage('Realtime channel timeout', {
            level: 'warning',
            tags: { module: 'realtime', channel: `chat-org-${orgId}` },
          });
        }
      });
  }

  private teardown(entry: OrgEntry) {
    if (entry.channel) {
      supabase.removeChannel(entry.channel);
      entry.channel = null;
    }
  }
}

export const chatRealtimeManager = new ChatRealtimeManager();
